import { ChangeEvent, FC, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import debounce from 'debounce'
import { nanoid } from 'nanoid'

import { copyFindExcursionsStore } from 'entities/excursions/models/copy-find-excursions/copy-find-excursions-store'
import { findExcursionsStore } from 'entities/excursions/models/find-excursions/find-excursions-store'
import { ISearchDirection } from 'entities/excursions/models/find-excursions/models'

import { SearchExcursions as SearchExcursionsApi } from 'shared/api/excursions'
import { SearchDirection } from 'shared/api/excursions/search/model'
import ImageGlass from 'shared/images/glasss.png'
import { EmptyResults } from 'shared/ui/empty-result'
import { ImageLazy } from 'shared/ui/image-lazy'
import { Loader } from 'shared/ui/loader'
import { SearchInput } from 'shared/ui/search-input'
import { SearchResultBlock } from 'shared/ui/search-result-block'

import { Props } from './model'
import { findExcursionInList } from './module'
import s from './styles.module.scss'

const TIMER = 1000

export const SearchExcursions: FC<Props> = ({ isSearchActive }) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [loading, setLoading] = useState<boolean>(false)
  const [listExcursions, setListExcursions] = useState<SearchDirection[]>()

  const [params] = useSearchParams()

  const isSearch = params.get('isSearch')

  const currentExcursionStore = isSearch
    ? copyFindExcursionsStore
    : findExcursionsStore

  const onSearchExcursions = async (event: ChangeEvent<HTMLInputElement>) => {
    const searchingValue = event?.target?.value

    isSearchActive(!!searchingValue?.length)

    if (!searchingValue?.length) {
      onReset()

      return
    }

    setLoading(true)

    try {
      const { data: resultSearch } =
        await SearchExcursionsApi.searchDirection(searchingValue)

      setListExcursions(resultSearch?.data)
    } catch {
    } finally {
      setLoading(false)
    }
  }

  const onReset = () => {
    setLoading(false)
    isSearchActive(false)
    setListExcursions(undefined)
  }

  const onSelectExcursion = (id: string) => {
    const excursion = findExcursionInList(id, listExcursions)

    const direction: ISearchDirection = {
      direction_name: excursion?.direction.name,
      name: excursion?.name,
    }

    currentExcursionStore.onSelectDirection(direction)
    navigate(-1)
  }

  const preparedListExcursions = useMemo(
    () =>
      listExcursions?.map((excursion) => ({
        id: excursion?.id,
        title: excursion.direction.name,
        subTitle: excursion?.name,
        image: excursion.preview_image_fullpath,
      })),
    [listExcursions],
  )

  return (
    <div className={s.container}>
      <div className={s.search}>
        <SearchInput
          onReset={onReset}
          placeholder={t('nav_bar_search')}
          onChange={debounce(onSearchExcursions, TIMER)}
        />

        <div className={s.result}>
          {loading ? (
            <Loader
              marginTop={200}
              type="excursions"
            />
          ) : (
            <>
              {preparedListExcursions?.map((excursion) => (
                <SearchResultBlock
                  key={nanoid()}
                  onSelect={() => onSelectExcursion(excursion.id)}
                  {...excursion}
                />
              ))}
            </>
          )}
        </div>

        {!loading && preparedListExcursions?.length === 0 && (
          <div className={s.emptyBlock}>
            <EmptyResults
              image={
                <ImageLazy
                  src={ImageGlass}
                  className={s.image}
                  alt="empty"
                />
              }
              title={t('not_fount_title')}
              subTitle={t('not_fount_sub_title')}
            />
          </div>
        )}
      </div>
    </div>
  )
}
